import { Logger, LoggerService } from '@Shared/logger';
import { OfferDetails, OfferDictionary, OfferProps, QueuePaywayTokenResponse, RawDomOffer } from '../_models';
import { LocalStorage } from '@Shared/storage/local_storage';
import { STORAGE } from '../../../core/models';
import { buildOfferFromDom, isDomOffer, buildOfferFromNaviga } from '../helpers/apple-pay.dom';
import { SETTINGS } from '@Config';
import { NavigaOffer } from '../_models'
import { getBaseSiteUrl } from '@Shared/utils/data';;
import { UIUtil } from '@Shared/utils/ui';

/**
 * Repository base class for Payway.
 */
export abstract class PaywayRepository {
	private LoggerBase: Logger = LoggerService.createLogger('PaywayRepository');

	protected queuePaywayTokenResponse: QueuePaywayTokenResponse | null = null;
	protected offerDetails: OfferDetails | null = null;
	protected sourceId: number | null = null;
	protected applePayPayment: ApplePayJS.ApplePayPayment | null = null;
	protected offerDictionary: OfferProps | null = null;
	protected appleSession: ApplePaySession | null = null;
	protected localStorageManger: LocalStorage;
	protected twoStepPaywall: boolean = false;
	protected offerGroupId: number | null = null;
	protected offerId: number | null = null;
	protected terms: string = '';

	constructor() {
		this.LoggerBase.trace('****  CTOR ****');
		this.localStorageManger = new LocalStorage();
	}

	protected async resolveOfferDetails(buttonSelector: string, terms: string): Promise<OfferDetails | null> {
		const parsedSiteName = getBaseSiteUrl();

		const offerId = this.offerId;
		const offerGroupId = this.offerGroupId;
		if (!offerId || !offerGroupId) {
			this.LoggerBase.trace('APPLE PAY | Missing offer IDs, cannot resolve offer');
			return null;
		}

		// 1. Offer details from the self-managed paywall
		const dataset = UIUtil.getDataset(buttonSelector);
		if (dataset && isDomOffer(dataset)) {
			const domOffer = buildOfferFromDom(dataset, offerId, offerGroupId, terms);
			if (domOffer) {
				this.LoggerBase.trace('APPLE PAY | Using offer details from self-manage paywall', domOffer);
				return domOffer;
			}
		}

		// 2. Offer details from Naviga via Entitler
		const navigaOffer = await this.fetchNavigaOffer(offerId, offerGroupId, parsedSiteName);
		if (navigaOffer) {
			const offer = buildOfferFromNaviga(navigaOffer, offerId, offerGroupId, terms);
			if (offer) {
				this.LoggerBase.trace('APPLE PAY | Using offer details from Naviga', offer);
				return offer;
			}
		}

		// 3. Local fallback
		this.LoggerBase.trace('APPLE PAY | Falling back to local billing data');
		return this.getBillingDetails(offerGroupId, offerId);
	}

	protected getBillingDetails(offerGroup: number, offerCode: number): OfferDetails | null {
		// Implementation for fetching billing details based on the offer group
		const offerDictionary: OfferDictionary = {
			1852: {
				4638: {
					billingAgreement: `AUTOMATIC RENEWAL TERMS:  After the 1-year $1.00 introductory rate, your payment method will be automatically charged $21.99 every Month.
					By subscribing, you agree to the Automatic Renewal Terms of this offer, the Terms of Use, and Arbitration and Refund Terms. Your subscription will renew automatically, and you will be charged up to seven days in advance of your renewal date until you cancel.
					You may cancel at any time by calling Subscriber Services at 570-628-5985 or by selecting "Cancel Subscription" in your account settings.
					The cancellation goes into effect at the start of your following billing cycle.`,
					offerId: 4638,
					promoDuration: '1 year',
					paymentDescription: '$1.00 for 1-year of access, then $21.99 per month subscription',
					trialIsRecurring: true,
					promo: {
						label: 'Standard Digital: 1 Year Trial',
						amount: '1.00',
						paymentTiming: 'recurring',
						recurringPaymentIntervalUnit: 'year',
						recurringPaymentIntervalCount: 1,
					},
					regularBilling: {
						label: 'Regular Billing',
						amount: '21.99',
						paymentTiming: 'recurring',
						recurringPaymentIntervalUnit: 'day',
						recurringPaymentIntervalCount: 28
					},
				},
			},
			2448: {
				19015: {
					billingAgreement: `AUTOMATIC RENEWAL TERMS:  After the 1-year $1.00 introductory rate, your payment method will be automatically charged $21.99 every Month.
					By subscribing, you agree to the Automatic Renewal Terms of this offer, the Terms of Use, and Arbitration and Refund Terms. Your subscription will renew automatically, and you will be charged up to seven days in advance of your renewal date until you cancel.
					You may cancel at any time by calling Subscriber Services at 570-628-5985 or by selecting "Cancel Subscription" in your account settings.
					The cancellation goes into effect at the start of your following billing cycle.`,
					offerId: 19015,
					promoDuration: '1 year',
					paymentDescription: '$1.00 for 1-year of access, then $21.99 per month subscription',
					trialIsRecurring: true,
					promo: {
						label: 'Standard Digital: 1 Year Trial',
						amount: '1.00',
						paymentTiming: 'recurring',
						recurringPaymentIntervalUnit: 'year',
						recurringPaymentIntervalCount: 1,
					},
					regularBilling: {
						label: 'Regular Billing',
						amount: '21.99',
						paymentTiming: 'recurring',
						recurringPaymentIntervalUnit: 'day',
						recurringPaymentIntervalCount: 28
					},
				},
			},
			2783: {
				21800: {
					billingAgreement: `AUTOMATIC RENEWAL TERMS:  After the 1-year $1.00 introductory rate, your payment method will be automatically charged $21.99 every Month.
					By subscribing, you agree to the Automatic Renewal Terms of this offer, the Terms of Use, and Arbitration and Refund Terms. Your subscription will renew automatically, and you will be charged up to seven days in advance of your renewal date until you cancel.
					You may cancel at any time by calling Subscriber Services at 570-628-5985 or by selecting "Cancel Subscription" in your account settings.
					The cancellation goes into effect at the start of your following billing cycle.`,
					offerId: 21800,
					promoDuration: '1 year',
					paymentDescription: '$1.00 for 1-year of access, then $21.99 per month subscription',
					trialIsRecurring: true,
					promo: {
						label: 'Standard Digital: 1 Year Trial',
						amount: '1.00',
						paymentTiming: 'recurring',
						recurringPaymentIntervalUnit: 'year',
						recurringPaymentIntervalCount: 1,
					},
					regularBilling: {
						label: 'Regular Billing',
						amount: '21.99',
						paymentTiming: 'recurring',
						recurringPaymentIntervalUnit: 'day',
						recurringPaymentIntervalCount: 28
					},
				},
			},
			2889: {
				22798: {
					billingAgreement: this.terms,
					offerId: 22798,
					promoDuration: '1 year',
					paymentDescription: '$3 for 1 year introductory rate, then your payment method will be charged $21.99 monthly',
					trialIsRecurring: true,
					promo: {
						label: 'Standard Digital: 1 Year Trial',
						amount: '3.00',
						paymentTiming: 'recurring',
						recurringPaymentIntervalUnit: 'year',
						recurringPaymentIntervalCount: 1,
					},
					regularBilling: {
						label: 'Regular Billing',
						amount: '21.99',
						paymentTiming: 'recurring',
						recurringPaymentIntervalUnit: 'day',
						recurringPaymentIntervalCount: 30,
						type: 'final'
					},
				},
				22797: {
					billingAgreement: this.terms,
					offerId: 22797,
					promoDuration: '1 year',
					paymentDescription: '$3 for 1 year introductory rate, then your payment method will be charged $21.99 monthly',
					trialIsRecurring: true,
					promo: {
						label: 'Standard Digital: 1 Year Trial',
						amount: '3.00',
						paymentTiming: 'recurring',
						recurringPaymentIntervalUnit: 'year',
						recurringPaymentIntervalCount: 1,
					},
					regularBilling: {
						label: 'Regular Billing',
						amount: '21.99',
						paymentTiming: 'recurring',
						recurringPaymentIntervalUnit: 'day',
						recurringPaymentIntervalCount: 30,
						type: 'final'
					},
				},
				22799: {
					billingAgreement: this.terms,
					offerId: 22799,
					promoDuration: '1 year',
					paymentDescription: '$3 for 1 year introductory rate, then your payment method will be charged $21.99 monthly',
					trialIsRecurring: true,
					promo: {
						label: 'Standard Digital: 1 Year Trial',
						amount: '3.00',
						paymentTiming: 'recurring',                 // required for recurring flows
						recurringPaymentIntervalUnit: 'year',      // use 'year' for a 1-year trial
						recurringPaymentIntervalCount: 1,        // every 1 year
					},
					regularBilling: {
						label: 'Regular Billing',
						amount: '21.99',
						paymentTiming: 'recurring',
						recurringPaymentIntervalUnit: 'day',
						recurringPaymentIntervalCount: 30,
						type: 'final'
					},
				},
				22800: {
					billingAgreement: this.terms,
					offerId: 22800,
					promoDuration: '1 year',
					paymentDescription: '$3 for 1 year introductory rate, then your payment method will be charged $21.99 monthly',
					trialIsRecurring: true,
					promo: {
						label: 'Standard Digital: 1 Year Trial',
						amount: '3.00',
						paymentTiming: 'recurring',
						recurringPaymentIntervalUnit: 'year',
						recurringPaymentIntervalCount: 1,
					},
					regularBilling: {
						label: 'Regular Billing',
						amount: '21.99',
						paymentTiming: 'recurring',
						recurringPaymentIntervalUnit: 'day',
						recurringPaymentIntervalCount: 30,
						type: 'final'
					},
				},
			},

		};
		const entry = offerDictionary[offerGroup]?.[offerCode];
		if (!entry) {
			return null;
		}
		const offerExpiresDate = this.getFutureDate(entry.promoDuration);

		const offer: OfferDetails = {
			...entry,
			offerGroupId: Number(offerGroup),
			promo: { ...entry.promo },
			regularBilling: { ...entry.regularBilling, recurringPaymentStartDate: offerExpiresDate },
			};
			if (offer.trialIsRecurring) {
			offer.promo.recurringPaymentEndDate = offerExpiresDate;
		}
		return offer;
	}

	private  async fetchNavigaOffer( offerId: number, offerGroupId: number, siteName: string): Promise<NavigaOffer | null>{
		try {
			const endpoint =  `${SETTINGS.entitler.baseUrl}payway/get_offer`;
			const getOfferBody = {
				domain: siteName,
				offerGroupId: offerGroupId,
				offerId: offerId
			}
			const response = await fetch(endpoint, {
				method: 'POST',
				headers: { 'X-Api-Key': SETTINGS.entitler.apiKey },
				body: JSON.stringify(getOfferBody),  
			});
			if (!response.ok) {
				this.LoggerBase.trace('APPLE PAY | Naviga request failed', response.status);
				return null;
			}
			const body = await response.json();
			const offer: NavigaOffer | null = body?.data?.Result?.Offers?.[0] ?? null;
			this.LoggerBase.trace('APPLE PAY | Result from Naviga server call', offer);
			return offer;
		} catch (error) {
			this.LoggerBase.error('APPLE PAY | Error fetching Naviga offer', error);
			return null;
		}
	} 

	private getFutureDate(duration: string): Date {
		const [amount, unit] = duration.split(' ');
		const value = parseInt(amount, 10);

		const futureDate = new Date();
		const lowerUnit = unit.toLowerCase();

		if (lowerUnit.startsWith('year')) {
			futureDate.setFullYear(futureDate.getFullYear() + value);
		} else if (lowerUnit.startsWith('month')) {
			futureDate.setMonth(futureDate.getMonth() + value);
		} else if (lowerUnit.startsWith('week')) {
			futureDate.setDate(futureDate.getDate() + value * 7);
		} else if (lowerUnit.startsWith('day')) {
			futureDate.setDate(futureDate.getDate() + value);
		}

		return futureDate;
	}

	public hasPendingPaymentAuthorization(): boolean {
		const pendingAuthorization = this.localStorageManger.get(STORAGE.applePayNewStartKey);
		return !!pendingAuthorization;
	}

}
